/**
 * Polymarket URL helpers.
 *
 * The trading-bot stores **synthetic** slugs in the DB that match
 * `synthetic_slug_format` in `contracts/markets.json`. As of
 * migration 0007 the btc-5m / eth-5m synthetic format is unified
 * with `slug_pattern` (epoch-based, e.g. `eth-updown-5m-1782625800`),
 * so the DB slug == the Polymarket slug for those two markets. The
 * 1h markets still legitimately diverge: their DB slug is
 * `<asset>-updown-1h-{epoch}` while Polymarket indexes them under
 * `<asset>-up-or-down-{date_long}` (ET-timezone month/day/am-pm).
 *
 * Linking with the synthetic slug for divergent markets produces a
 * 404 on Polymarket. These helpers translate
 * `(market.slug_pattern, start)` into the real Polymarket-facing
 * slug regardless of which market the window came from.
 *
 * Mirrors `_render_pattern` in `trading_bot/markets.py`. Supported
 * tokens:
 *
 *   {yyyymmdd}  — UTC calendar date, no separators ("20260628")
 *   {hhmm}      — UTC hour+minute, no separators ("0130")
 *   {epoch}     — UNIX timestamp in seconds, UTC ("1782625800")
 *   {date_long} — `month-d-yyyy-Iam|pm-et` rendered in
 *                 America/New_York (Polymarket names hourly markets in
 *                 US Eastern time), e.g. "june-27-2026-11pm-et".
 */

const POLYMARKET_EVENT_BASE = "https://polymarket.com/event";

function renderDateLong(start: Date): string {
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    hour12: true,
  });
  const parts = fmt.formatToParts(start);
  const get = (t: string): string => parts.find((p) => p.type === t)?.value ?? "";
  const month = get("month").toLowerCase();
  const day = get("day");
  const year = get("year");
  const hour = get("hour");
  const ampm = get("dayPeriod").toLowerCase();
  return `${month}-${day}-${year}-${hour}${ampm}-et`;
}

/**
 * Render a `slug_pattern` (from the markets catalogue) into the
 * Polymarket-facing slug for the given window start.
 */
export function renderPolymarketSlug(pattern: string, start: Date): string {
  if (!Number.isFinite(start.getTime())) return pattern;
  const y = start.getUTCFullYear();
  const m = String(start.getUTCMonth() + 1).padStart(2, "0");
  const d = String(start.getUTCDate()).padStart(2, "0");
  const hh = String(start.getUTCHours()).padStart(2, "0");
  const mm = String(start.getUTCMinutes()).padStart(2, "0");
  const yyyymmdd = `${y}${m}${d}`;
  const hhmm = `${hh}${mm}`;
  const epoch = String(Math.floor(start.getTime() / 1000));
  return pattern
    .replace("{yyyymmdd}", yyyymmdd)
    .replace("{hhmm}", hhmm)
    .replace("{epoch}", epoch)
    .replace("{date_long}", renderDateLong(start));
}

/**
 * Parse a synthetic slug into the window start `Date`. Handles the
 * formats the bot emits today and the one pre-migration-0007 legacy
 * export files / hand-copied URLs may still carry:
 *
 *   - `<asset>-updown-<n><unit>-<epoch>` — current format for every
 *     market (post-0007 the 5m markets use this too).
 *   - `<asset>-up-or-down-YYYYMMDD-HHMMZ` — legacy 5m shape kept as a
 *     fallback so URLs that escaped the DB rename keep linking.
 *
 * Returns null when neither pattern matches.
 */
export function parseStartFromSyntheticSlug(slug: string): Date | null {
  const epochMatch = slug.match(/-(\d{10})$/);
  if (epochMatch?.[1]) {
    const sec = Number(epochMatch[1]);
    if (Number.isFinite(sec)) return new Date(sec * 1000);
  }
  const compact = slug.match(/(\d{8})-(\d{4})z?$/i);
  if (compact?.[1] && compact?.[2]) {
    const ymd = compact[1];
    const hhmm = compact[2];
    const iso = `${ymd.slice(0, 4)}-${ymd.slice(4, 6)}-${ymd.slice(6, 8)}T${hhmm.slice(0, 2)}:${hhmm.slice(2, 4)}:00Z`;
    const t = Date.parse(iso);
    if (Number.isFinite(t)) return new Date(t);
  }
  return null;
}

/**
 * Build the public Polymarket event URL for a window.
 *
 * Prefers `slugPattern + start` (the real Polymarket slug). Falls back
 * to the synthetic `slug` only when we have no catalogue entry — that
 * URL likely 404s but at least it's not broken JS.
 */
export function polymarketEventUrl(opts: {
  slug: string;
  slugPattern?: string | null;
  windowStartIso?: string | null;
}): string {
  const { slug, slugPattern, windowStartIso } = opts;
  let start: Date | null = null;
  if (windowStartIso) {
    const ms = Date.parse(windowStartIso);
    if (Number.isFinite(ms)) start = new Date(ms);
  }
  if (!start) start = parseStartFromSyntheticSlug(slug);
  if (slugPattern && start) {
    const rendered = renderPolymarketSlug(slugPattern, start);
    return `${POLYMARKET_EVENT_BASE}/${encodeURIComponent(rendered)}`;
  }
  return `${POLYMARKET_EVENT_BASE}/${encodeURIComponent(slug)}`;
}
